import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { AccountingCustomerRecord, FinanceApiService } from '../../features/finance/finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-customer-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="displayName"
      optionValue="key"
      [placeholder]="t('finance.customers.selectPlaceholder')"
      [showClear]="true"
      [filter]="true"
      filterBy="displayName,key"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`
    .selector { width: 100%; }
  `]
})
export class CustomerSelectorComponent implements ControlValueAccessor {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  readonly loading = signal(false);
  readonly options = signal<AccountingCustomerRecord[]>([]);
  readonly value = signal<string | null>(null);
  @Input() appendTo: 'body' | 'self' = 'body';
  @Input() disabled = false;

  /** Model value from parent (survives async option loads). */
  private pendingValue: string | null = null;
  private customerResolveToken = 0;
  private optionsLoaded = false;

  constructor() {
    this.load();
  }

  writeValue(value: string | null): void {
    const normalized = value?.trim() || null;
    this.pendingValue = normalized;
    this.customerResolveToken++;
    if (this.optionsLoaded) {
      this.reconcileSelection();
    } else {
      this.value.set(normalized);
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (this.optionsLoaded && (this.pendingValue ?? this.value())) {
      this.reconcileSelection();
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  onValueChange(value: string | null): void {
    this.pendingValue = value;
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  private load(): void {
    this.loading.set(true);
    this.api.listAccountingCustomers().subscribe({
      next: rows => {
        this.optionsLoaded = true;
        this.options.set(rows);
        this.reconcileSelection();
        this.loading.set(false);
      },
      error: () => {
        this.optionsLoaded = true;
        this.options.set([]);
        this.reconcileSelection();
        this.loading.set(false);
      }
    });
  }

  private reconcileSelection(): void {
    const selected = this.pendingValue ?? this.value();
    if (!selected) {
      this.value.set(null);
      return;
    }

    const rows = this.options();
    if (this.applySelectionFromOptions(rows, selected)) {
      return;
    }

    this.fetchAndInjectCustomer(selected);
  }

  private applySelectionFromOptions(rows: AccountingCustomerRecord[], selected: string): boolean {
    const match = rows.find(row => this.sameCustomerKey(row.key, selected));
    if (!match) {
      return false;
    }

    this.pendingValue = match.key;
    this.commitSelectValue(match.key, true);
    return true;
  }

  private fetchAndInjectCustomer(selected: string): void {
    const token = ++this.customerResolveToken;
    this.api.getPlatformCustomer(selected).subscribe({
      next: customer => {
        if (token !== this.customerResolveToken) {
          return;
        }

        const current = this.pendingValue ?? this.value();
        if (!current || !this.sameCustomerKey(customer.id, current)) {
          return;
        }

        const record: AccountingCustomerRecord = {
          key: customer.id,
          displayName: customer.displayName,
          code: customer.customerCode,
          status: String(customer.status)
        };
        this.options.update(rows => {
          if (rows.some(row => this.sameCustomerKey(row.key, record.key))) {
            return rows;
          }
          return [...rows, record];
        });
        this.pendingValue = record.key;
        this.commitSelectValue(record.key, true);
      },
      error: () => {}
    });
  }

  /** PrimeNG Select may miss a value set before options exist; nudge after options are ready. */
  private commitSelectValue(key: string, refreshDisplay = false): void {
    this.value.set(key);
    if (refreshDisplay) {
      this.refreshSelectDisplay(key);
    }
  }

  private refreshSelectDisplay(key: string): void {
    queueMicrotask(() => {
      if ((this.pendingValue ?? this.value()) !== key) {
        return;
      }

      this.value.set(null);
      queueMicrotask(() => {
        if ((this.pendingValue ?? this.value()) === key) {
          this.value.set(key);
        }
      });
    });
  }

  private sameCustomerKey(left: string | null | undefined, right: string | null | undefined): boolean {
    if (!left || !right) {
      return left === right;
    }
    return left.trim().toLowerCase() === right.trim().toLowerCase();
  }
}
